import { redirect } from '@tanstack/react-router'

import { ensureRouteData } from '@/app/query-client'
import {
  paymentsQueryOptions,
  plansQueryOptions,
  subscriptionMeQueryOptions,
} from '@/features/subscriptions/api/queries'
import type { RouterContext } from '@/routes/layouts/root'

/**
 * The `/subscription/manage` loader.
 *
 * `subscriptionMe` goes first and ALONE: a Free member has no plan to manage
 * (BUILD-BRIEF), and reading it out of the query cache here — rather than
 * branching inside the component after it has already rendered a frame of
 * the paid layout — is what keeps a Free member from ever seeing this page
 * flash before `/subscription` replaces it. `replace: true` so Back does not
 * bounce them straight into the redirect again.
 *
 * `plans` (the catalogue, for the Price row's join) and `payments` (the
 * "Paid with" row and the billing-history receipt count) are both MAIN too,
 * but only fetched once a paid tier is confirmed — a Free member's redirect
 * never pays for either request.
 */
export async function loadManagePlan({
  context: { queryClient },
  deps: { billing },
}: {
  context: RouterContext
  deps: { billing: boolean | undefined }
}): Promise<void> {
  const entitlement = await ensureRouteData(queryClient, subscriptionMeQueryOptions())
  if (entitlement.tier === 'free') {
    // A Free member asking for their billing history still gets it, over the plans.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/subscription', search: { billing }, replace: true })
  }

  await Promise.all([
    ensureRouteData(queryClient, plansQueryOptions()),
    ensureRouteData(queryClient, paymentsQueryOptions()),
  ])
}
